/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { action, makeObservable, observable } from 'mobx';

import { BaseAddon } from '../base-addon';
import { CommonSupplier, EnumDescriptorsMap } from '../../suppliers/web';
import { isErrorThatNeedToBeShown } from '../../core';

import type { AddonContext } from '../base-addon';
import type { RequestSuccessResponse } from '../../interfaces';
import type { EnumMap, PortalEnumsConfig } from './interfaces';

export class PortalEnumsAddon extends BaseAddon {

    protected readonly url: string;
    protected readonly commonSupplier: CommonSupplier;

    protected readonly webControllersEnumsMap: any; // @TODO Remove with ext.js

    @observable protected enumDescriptors: EnumDescriptorsMap = {};

    constructor(addonContext: AddonContext, portalEnumsConfig: PortalEnumsConfig = {}) {
        super(addonContext);

        makeObservable(this);

        const {
            url = '/Common/GetEnumDescriptors',
            webControllersEnumsMap,
        } = portalEnumsConfig ?? {};

        this.url = url;
        this.commonSupplier = new CommonSupplier(this.services.transportService);

        this.webControllersEnumsMap = webControllersEnumsMap ?? {};
    }

    @action
    init(): Promise<void> {
        return this.commonSupplier.getEnumDescriptors(this.url, { disableGlobalFilters: true })
            .then((resp: RequestSuccessResponse<EnumDescriptorsMap>) => {
                this.setEnumDescriptors(resp.data);
            })
            .catch((err) => {
                isErrorThatNeedToBeShown(err) && console.error(err);
            });
    }

    @action
    destroy(): void {
        this.setEnumDescriptors({});
    }

    @action.bound
    setEnumDescriptors(value: EnumDescriptorsMap): void {
        this.enumDescriptors = value;
    }

    getEnumDescriptors() {
        return this.enumDescriptors;
    }

    getEnumDescription(enumName: string, enumValue: string): string {
        const fixedEnumName = `${enumName.charAt(0).toLowerCase()}${enumName.slice(1)}`;

        if (this.enumDescriptors[fixedEnumName]) {
            return this.enumDescriptors[fixedEnumName]
                .find(enumDescriptorItem => enumDescriptorItem.literalValue === enumValue)
                .description;
        }
    }

    getEnumDescriptionByNumberEnum(enumName: string, enumNumValue: number): string {
        const enumDescriptors = this.enumDescriptors;
        const fixedEnumName = `${enumName.charAt(0).toLowerCase()}${enumName.slice(1)}`;

        if (enumDescriptors[fixedEnumName]) {
            return enumDescriptors[fixedEnumName]
                .find(enumDescriptorItem => enumDescriptorItem.intValue === enumNumValue)
                ?.description;
        }
    }

    getEnumIntValue(enumName: string, enumValue: string): number {
        const enumDescriptors = this.enumDescriptors;
        const fixedEnumName = `${enumName.charAt(0).toLowerCase()}${enumName.slice(1)}`;

        if (enumDescriptors[fixedEnumName]) {
            return enumDescriptors[fixedEnumName]
                .find(enumDescriptorItem => enumDescriptorItem.literalValue === enumValue)
                ?.intValue;
        }
    }

    getEnumStringValue(enumName: string, enumValue: number): string {
        const enumDescriptors = this.enumDescriptors;
        const fixedEnumName = `${enumName.charAt(0).toLowerCase()}${enumName.slice(1)}`;

        if (enumDescriptors[fixedEnumName]) {
            return enumDescriptors[fixedEnumName]
                .find(enumDescriptorItem => enumDescriptorItem.intValue === enumValue)
                ?.literalValue;
        }
    }

    replaceEnumsInData(dataType: string, data: Object): void {
        const enumDescriptors = this.enumDescriptors;

        const handleDataItem = (dataItem: Object, enumMap: EnumMap, stepI = 0) => {
            const isLastStep = stepI === enumMap.path.length - 1;
            if (isLastStep) {
                const pathValue = enumMap.path[stepI];
                const extractEnumFromDescriptor = (enumName, enumVal, pathVal) => {
                    const enumDescriptor = enumDescriptors[enumName]
                        .find(enumDescriptorItem => enumDescriptorItem.literalValue.toLowerCase() === enumVal.toLowerCase());

                    if (enumDescriptor) {
                        dataItem[pathVal] = enumDescriptor.intValue;
                        if (typeof pathVal === 'string') {
                            dataItem[`${pathVal}_literalValue`] = enumDescriptor.literalValue;
                            dataItem[`${pathVal}_description`] = enumDescriptor.description;
                        }
                    }
                };

                const isArrayStep = pathValue === '$i';

                if (isArrayStep && dataItem) {
                    const dataArr = dataItem as string[];
                    dataArr.forEach((enumValue, index) => {
                        if (enumValue) {
                            extractEnumFromDescriptor(enumMap.enumName, enumValue, index);
                        }
                    });
                    return;
                }

                const enumValue = dataItem ? dataItem[pathValue] as string : null;
                if (enumValue) {
                    if (enumDescriptors[enumMap.enumName]) {
                        extractEnumFromDescriptor(enumMap.enumName, enumValue, pathValue);
                    } else {
                        if (enumMap.enumName.indexOf('nullable') === 0) {
                            const enumName = enumMap.enumName.replace(/^nullable([\d\D])([\d\D]+$)/, (a, b, c) => b.toLowerCase() + c);
                            extractEnumFromDescriptor(enumName, enumValue, pathValue);
                            console.warn(`(Handled) No enum "${enumMap.enumName}" in enum descriptors. Data: "${dataType}"`);
                        } else {
                            console.error(`No enum "${enumMap.enumName}" in enum descriptors. Data: "${dataType}"`);
                        }
                    }
                }
            } else {
                const isArrayStep = enumMap.path[stepI] === '$i';
                if (isArrayStep) {
                    const dataItemArray = dataItem as Array<Object>;
                    if (dataItemArray) {
                        dataItemArray.forEach(dataSubItem => handleDataItem(dataSubItem, enumMap, stepI + 1));
                    }
                } else {
                    if (dataItem) {
                        handleDataItem(dataItem[enumMap.path[stepI]], enumMap, stepI + 1);
                    }
                }
            }
        };

        if (dataType in this.webControllersEnumsMap) {
            this.webControllersEnumsMap[dataType].response.forEach(
                enumMap => handleDataItem(data, enumMap)
            );

        } else {
            console.error(`No model "${dataType}" in webControllersEnumsMap. Add model to list modelsForHandle in handleSwaggerConfig.js`);
        }
    }

    restoreStringEnumsInData(dataType: string, data: Object): void {
        const enumDescriptors = this.enumDescriptors;


        const handleDataItem = (dataItem: Object, enumMap: EnumMap, stepI = 0) => {
            const isLastStep = stepI === enumMap.path.length - 1;
            const handleEnumValue = (obj: Object, propName: string|number) => {
                const enumValue = obj[propName] as number;
                if (typeof enumValue === 'number') {
                    if (!enumDescriptors[enumMap.enumName]) {
                        console.error(`No enum "${enumMap.enumName}" in enum descriptors. Data type: "${dataType}"`);
                    } else {
                        const enumDescriptor = enumDescriptors[enumMap.enumName]
                            .find(enumDescriptorItem => enumDescriptorItem.intValue === enumValue);

                        if (enumDescriptor) {
                            obj[propName] = enumDescriptor.literalValue;
                        }
                    }
                }
            };

            if (!dataItem) {
                return;
            }

            if (isLastStep) {
                const pathValue = enumMap.path[stepI];
                if (pathValue === '$i') {
                    (dataItem as Array<string>).forEach((param, i) => handleEnumValue(dataItem, i));
                } else {
                    handleEnumValue(dataItem, pathValue);
                }
            } else {
                const isArrayStep = enumMap.path[stepI] === '$i';
                if (isArrayStep) {
                    const dataItemArray = dataItem as Array<Object>;
                    if (dataItemArray) {
                        dataItemArray.forEach(dataSubItem => handleDataItem(dataSubItem, enumMap, stepI + 1));
                    }
                } else {
                    if (dataItem) {
                        handleDataItem(dataItem[enumMap.path[stepI]], enumMap, stepI + 1);
                    }
                }
            }
        };


        if (dataType in this.webControllersEnumsMap) {
            this.webControllersEnumsMap[dataType].response.forEach(
                enumMap => handleDataItem(data, enumMap)
            );

        } else {
            console.error(`No model "${dataType}" in webControllersEnumsMap. Add model to list modelsForHandle in handleSwaggerConfig.js`);
        }
    }

}
